import React, { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import Typography from "./typography";

type InputSize = "sm" | "md" | "lg";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  helperText?: string;
  error?: string;
  size?: InputSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  containerClassName?: string;
  labelClassName?: string;
  inputContainerClassName?: string;
  helperTextClassName?: string;
}

const sizeClasses: Record<InputSize, string> = {
  sm: "py-1.5 px-2 text-sm",
  md: "py-2 px-3 text-base",
  lg: "py-3 px-4 text-lg",
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      size = "md",
      leftIcon,
      rightIcon,
      fullWidth = false,
      className,
      containerClassName,
      labelClassName,
      inputContainerClassName,
      helperTextClassName,
      disabled,
      ...props
    },
    ref
  ) => {
    const inputClasses = twMerge(
      // Base styles
      "border rounded-md outline-none transition-all duration-200 font-sans",
      "focus:ring-0 focus:ring-transparent focus:border-teal-500",
      "placeholder:text-text-light",

      // Size
      sizeClasses[size],

      // Width
      fullWidth ? "w-full" : "",

      // Icon padding adjustments
      leftIcon ? "pl-10" : "",
      rightIcon ? "pr-10" : "",

      // State styles
      disabled ? "text-gray-400 cursor-not-allowed" : "text-text",
      error
        ? "border-red-500 focus:ring-red-200 focus:border-red-500"
        : "border-teal-100 hover:border-teal-200",

      // Additional custom classes
      className
    );

    const containerClasses = twMerge(
      "flex flex-col",
      fullWidth ? "w-full" : "",
      containerClassName
    );

    const inputContainerClasses = twMerge(
      "relative flex items-center",
      inputContainerClassName
    );

    const labelClasses = twMerge(
      "block font-medium text-emerald-700 mb-1",
      error ? "text-red-500" : "",
      labelClassName
    );

    const helperTextClasses = twMerge(
      "mt-1 text-sm",
      error ? "text-red-500" : "text-emerald-600",
      helperTextClassName
    );

    return (
      <div className={containerClasses}>
        {label && (
          <label htmlFor={props.id} className={labelClasses}>
            <Typography variant="caption">{label}</Typography>
          </label>
        )}

        <div className={inputContainerClasses}>
          {leftIcon && (
            <div className="absolute left-3 flex items-center justify-center text-teal-500">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            className={inputClasses}
            disabled={disabled}
            {...props}
          />

          {rightIcon && (
            <div className="absolute right-3 flex items-center justify-center text-teal-500">
              {rightIcon}
            </div>
          )}
        </div>

        {(helperText || error) && (
          <Typography variant="caption" className={helperTextClasses}>
            {error || helperText}
          </Typography>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
